import React, { useState, useEffect } from 'react';
import {
  Card,
  Form,
  Button,
  message,
  Spin,
  Typography,
  Row,
  Col,
  Divider,
  Switch,
  Table,
  Tag,
  Space,
  TimePicker,
  Radio,
} from 'antd';
import {
  RobotOutlined,
  ReloadOutlined,
  SendOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  MessageOutlined,
} from '@ant-design/icons';
import { apiClient } from '../../config/apiClient';
import MainLayout from '../layout/layout';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

const AsistenciaBot = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [enviando, setEnviando] = useState(false);
  const [mensajes, setMensajes] = useState([]);
  const [mensajesLoading, setMensajesLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  const diaObjetivoActual = Form.useWatch('clasesDiaObjetivo', form) || 'manana';

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    loadConfiguracion();
    loadMensajes();
  }, []);

  const loadConfiguracion = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/asistencia-bot/configuracion');
      if (response.ok) {
        const data = await response.json();
        if (data && Object.keys(data).length > 0) {
          const horaEnvio = data.cfg_asist_hora_envio 
            ? dayjs(data.cfg_asist_hora_envio, 'HH:mm:ss')
            : dayjs('20:00', 'HH:mm');
          
          form.setFieldsValue({
            activo: data.cfg_asist_activo !== false,
            horaEnvio: horaEnvio,
            clasesDiaObjetivo: data.cfg_asist_dia_objetivo || 'manana',
          });
        }
      } else {
        const error = await response.json();
        message.error(error.message || 'Error al cargar la configuración');
      }
    } catch (error) {
      console.error('Error al cargar configuración:', error);
      message.error('Error al cargar la configuración');
    } finally {
      setLoading(false);
    }
  };

  const loadMensajes = async (page = 1, pageSize = 10) => {
    try {
      setMensajesLoading(true);
      const response = await apiClient.get(
        `/asistencia-bot/mensajes?page=${page}&pageSize=${pageSize}`
      );
      if (response.ok) {
        const data = await response.json();
        setMensajes(data.data || []);
        setPagination({
          current: data.page || 1,
          pageSize: data.pageSize || 10,
          total: data.total || 0,
        });
      } else {
        const error = await response.json();
        message.error(error.message || 'Error al cargar los mensajes');
      }
    } catch (error) {
      console.error('Error al cargar mensajes:', error);
      message.error('Error al cargar los mensajes');
    } finally {
      setMensajesLoading(false);
    }
  };

  const guardarConfiguracion = async () => {
    try {
      const values = form.getFieldsValue();
      
      const horaEnvio = values.horaEnvio 
        ? values.horaEnvio.format('HH:mm')
        : '20:00';
      
      const payload = {
        activo: values.activo !== false,
        horaEnvio: horaEnvio,
        clasesDiaObjetivo: values.clasesDiaObjetivo || 'manana',
      };
      
      const response = await apiClient.post('/asistencia-bot/configuracion', payload);
      if (response.ok) {
        message.success('Configuración guardada');
      } else {
        const error = await response.json();
        message.error(error.message || 'Error al guardar la configuración');
      }
    } catch (error) {
      console.error('Error al guardar configuración:', error);
      message.error('Error al guardar la configuración');
    }
  };

  const handleEnviarManual = async () => {
    try {
      setEnviando(true);
      const diaObjetivo = form.getFieldValue('clasesDiaObjetivo') || 'manana';
      const response = await apiClient.post('/asistencia-bot/enviar', { tipo: 'clases', diaObjetivo });
      if (response.ok) {
        const data = await response.json();
        const mensajesEnviados = data.data?.clases?.mensajesEnviados || 0;
        message.success(
          `Proceso completado: ${mensajesEnviados} mensajes enviados`
        );
        loadMensajes(pagination.current, pagination.pageSize);
      } else {
        const error = await response.json();
        message.error(error.message || 'Error al ejecutar el envío');
      }
    } catch (error) {
      console.error('Error al enviar mensajes:', error);
      message.error('Error al ejecutar el envío');
    } finally {
      setEnviando(false);
    }
  };

  const handleTableChange = (newPagination) => {
    loadMensajes(newPagination.current, newPagination.pageSize);
  };

  // Abrir WhatsApp con el número de teléfono
  const handleContactarWhatsApp = (telefono) => {
    if (!telefono) {
      message.warning('No hay número de teléfono disponible');
      return;
    }

    const numeroLimpio = telefono.replace(/[\s\-\(\)\+]/g, '');
    
    let numeroFormateado = numeroLimpio;
    if (numeroLimpio.startsWith('0')) {
      numeroFormateado = '54' + numeroLimpio.substring(1);
    } else if (!numeroLimpio.startsWith('54') && numeroLimpio.length <= 10) {
      numeroFormateado = '54' + numeroLimpio;
    }

    const url = `https://wa.me/${numeroFormateado}`;
    window.open(url, '_blank');
  };

  const getRespuestaTag = (respuesta) => {
    if (!respuesta) {
      return <Tag icon={<ClockCircleOutlined />} color="default">Pendiente</Tag>;
    }
    if (respuesta === 'SI') {
      return <Tag icon={<CheckCircleOutlined />} color="success">Sí</Tag>;
    }
    return <Tag icon={<CloseCircleOutlined />} color="error">No</Tag>;
  };

  const columns = [
    {
      title: 'Fecha Turno',
      dataIndex: 'msg_asist_fecha_turno',
      key: 'fecha_turno',
      render: (text) => {
        const fecha = dayjs(text);
        return fecha.format('DD/MM/YYYY');
      },
    },
    {
      title: 'Alumno',
      key: 'alumno',
      render: (record) => `${record.alu_nombre} ${record.alu_apellido}`,
    },
    {
      title: 'Teléfono',
      key: 'telefono',
      render: (record) => (
        <Space>
          <span>{record.alu_telefono || '-'}</span>
          {record.alu_telefono && (
            <Button
              type="link"
              icon={<MessageOutlined />}
              onClick={() => handleContactarWhatsApp(record.alu_telefono)}
              style={{ 
                padding: 0, 
                height: 'auto',
                color: '#25D366'
              }}
            >
              Contactar
            </Button>
          )}
        </Space>
      ),
    },
    {
      title: 'Turno',
      key: 'turno',
      render: (record) => {
        const hora = record.tur_hora_desde || 'N/A';
        const cancha = record.cancha_descripcion || 'N/A';
        return `${hora} - ${cancha}`;
      },
    },
    {
      title: 'Categoría',
      dataIndex: 'categoria_descripcion',
      key: 'categoria',
    },
    {
      title: 'Enviado',
      dataIndex: 'msg_asist_fecha_envio',
      key: 'fecha_envio',
      render: (text) => {
        if (!text) return '-';
        const fecha = dayjs(text);
        return fecha.format('DD/MM/YYYY HH:mm');
      },
    },
    {
      title: 'Respuesta',
      dataIndex: 'msg_asist_respuesta',
      key: 'respuesta',
      render: (respuesta) => getRespuestaTag(respuesta),
    },
    {
      title: 'Fecha Respuesta',
      dataIndex: 'msg_asist_fecha_respuesta',
      key: 'fecha_respuesta',
      render: (text) => {
        if (!text) return '-';
        const fecha = dayjs(text);
        return fecha.format('DD/MM/YYYY HH:mm');
      },
    },
  ];

  if (loading) {
    return (
      <MainLayout>
        <div style={{ 
          padding: '50px', 
          textAlign: 'center',
          minHeight: 'calc(100vh - 64px)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <Spin size="large" />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div style={{ padding: isMobile ? '16px' : '24px' }}>
        <Title level={isMobile ? 4 : 2} style={{ marginBottom: '24px', color: '#e0e0e0' }}>
          Confirmación de Clases
        </Title>

        <Row gutter={[24, 24]}>
          <Col xs={24} lg={16}>
            <Card
              title={
                <span>
                  <RobotOutlined style={{ marginRight: 8 }} />
                  Envío Automático de Mensajes para Clases
                </span>
              }
              extra={
                <Button
                  icon={<ReloadOutlined />}
                  onClick={loadConfiguracion}
                  size="small"
                >
                  Recargar
                </Button>
              }
            >
              <Text type="secondary" style={{ display: 'block', marginBottom: '24px' }}>
                Configura el envío automático de mensajes de confirmación de asistencia
                a los alumnos que tienen clases programadas. También puedes enviar manualmente
                para las clases de hoy o de mañana.
              </Text>

              <Form
                form={form}
                layout="vertical"
                onValuesChange={() => guardarConfiguracion()}
                autoComplete="off"
              >
                <Divider orientation="left">Configuración</Divider>
                
                <Form.Item
                  name="activo"
                  label={
                    <span>
                      <RobotOutlined style={{ marginRight: 8 }} />
                      Envío Automático Activado
                    </span>
                  }
                  valuePropName="checked"
                  style={{ marginBottom: '16px' }}
                >
                  <Switch 
                    checkedChildren="Activado" 
                    unCheckedChildren="Desactivado"
                    size="default"
                  />
                </Form.Item>
                <Text type="secondary" style={{ display: 'block', marginBottom: '24px', marginTop: '-8px' }}>
                  Cuando está activado, el sistema enviará automáticamente mensajes a los alumnos
                  para confirmar asistencia a sus clases según el día seleccionado.
                </Text>

                <Form.Item
                  name="clasesDiaObjetivo"
                  label="Enviar confirmación para las clases de"
                  initialValue="manana"
                >
                  <Radio.Group buttonStyle="solid" size="large">
                    <Radio.Button value="manana">Mañana (día siguiente)</Radio.Button>
                    <Radio.Button value="hoy">Hoy</Radio.Button>
                  </Radio.Group>
                </Form.Item>
                <Text type="secondary" style={{ display: 'block', marginBottom: '24px', marginTop: '-16px' }}>
                  Si seleccionas <strong>Mañana</strong>, los mensajes confirmarán las clases del día siguiente.
                  Si seleccionas <strong>Hoy</strong>, confirmarán las clases del mismo día.
                </Text>

                <Form.Item
                  name="horaEnvio"
                  label="Hora de Envío"
                  rules={[
                    { required: true, message: 'Selecciona la hora de envío' },
                  ]}
                >
                  <TimePicker
                    format="HH:mm"
                    style={{ width: '100%' }}
                    size="large"
                  />
                </Form.Item>
                <Text type="secondary" style={{ display: 'block', marginBottom: '24px', marginTop: '-16px' }}>
                  Hora a la que se enviarán los mensajes de confirmación (formato 24 horas).
                  Ejemplo: 20:00 para enviar a las 8:00 PM.
                </Text>

                <Divider orientation="left">Envío Manual</Divider>

                <Form.Item style={{ marginBottom: 0 }}>
                  <Button
                    type="default"
                    icon={<SendOutlined />}
                    onClick={handleEnviarManual}
                    loading={enviando}
                    size="large"
                  >
                    {enviando ? 'Enviando...' : `Enviar Ahora (${diaObjetivoActual === 'hoy' ? 'Hoy' : 'Mañana'})`}
                  </Button>
                </Form.Item>
              </Form>
            </Card>
          </Col>

          <Col xs={24} lg={8}>
            <Card title="Información">
              <Text type="secondary" style={{ display: 'block', marginBottom: '16px' }}>
                <strong>¿Cómo funciona?</strong>
              </Text>
              <Text type="secondary" style={{ display: 'block', marginBottom: '16px' }}>
                El sistema enviará automáticamente mensajes de WhatsApp a los alumnos
                que tienen clases programadas para el día configurado (hoy o mañana).
              </Text>
              <Text type="secondary" style={{ display: 'block', marginBottom: '16px' }}>
                Los alumnos recibirán un mensaje con los datos de la clase (fecha, hora, cancha, categoría)
                y dos botones: <strong>Sí</strong> o <strong>No</strong> para confirmar asistencia.
              </Text>
              <Text type="secondary" style={{ display: 'block', marginBottom: '16px' }}>
                La asistencia se actualizará automáticamente según la respuesta del alumno.
              </Text>
            </Card>
          </Col>
        </Row>

        <Row gutter={[24, 24]} style={{ marginTop: '24px' }}>
          <Col xs={24}>
            <Card
              title="Mensajes Enviados"
              extra={
                <Button
                  icon={<ReloadOutlined />}
                  onClick={() => loadMensajes(pagination.current, pagination.pageSize)}
                  size="small"
                  loading={mensajesLoading}
                >
                  Recargar
                </Button>
              }
            >
              <Table
                columns={columns}
                dataSource={mensajes}
                rowKey="msg_asist_id"
                loading={mensajesLoading}
                pagination={pagination}
                onChange={handleTableChange}
                scroll={{ x: 'max-content' }}
              />
            </Card>
          </Col>
        </Row>
      </div>
    </MainLayout>
  );
};

export default AsistenciaBot;
